import * as React from 'react'
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CrownRoundedIcon from '@mui/icons-material/EmojiEventsRounded'
import ExternalLinkRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import HubRoundedIcon from '@mui/icons-material/HubRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PaidRoundedIcon from '@mui/icons-material/PaidRounded'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded'
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded'
import SportsMmaRoundedIcon from '@mui/icons-material/SportsMmaRounded'
import useAutoTranslate from '../hooks/useAutoTranslate'

export const PRICING_PLANS = [
    {
        key: 'solo',
        name: 'Solo',
        bots: '1 Bot',
        concurrentBots: '1',
        description: 'One bot can run at a time',
        bestFor: 'Best for one main game account',
        monthlyPrice: '25',
        perBotPrice: '25',
        savings: '',
        accent: '#6ed4a5'
    },
    {
        key: 'duo',
        name: 'Duo',
        bots: '2 Bots',
        concurrentBots: '2',
        description: 'Two bots can run at the same time',
        bestFor: 'Best for two accounts or a small setup',
        monthlyPrice: '40',
        perBotPrice: '20',
        savings: 'Save 20%',
        accent: '#73c9f1'
    },
    {
        key: 'command',
        name: 'Command',
        bots: '4 Bots',
        concurrentBots: '4',
        description: 'Four bots can run at the same time',
        bestFor: 'Best for multi-account daily operation',
        monthlyPrice: '60',
        perBotPrice: '15',
        savings: 'Save 40%',
        accent: '#f4c95d',
        recommended: true
    },
    {
        key: 'no-limit',
        name: 'No Limit',
        bots: '10+ Bots',
        concurrentBots: '10+',
        description: 'Ten or more bots can run together',
        bestFor: 'Best for large groups and operators',
        monthlyPrice: '100+',
        perBotPrice: '10',
        savings: 'Best value',
        accent: '#d8a7ff'
    }
]

const INCLUDED_FEATURES = [
    { label: 'All core attack modules', icon: SportsMmaRoundedIcon, detail: 'Towers, camps, fortresses and events' },
    { label: 'Multi-kingdom automation', icon: HubRoundedIcon, detail: 'Configure every supported kingdom' },
    { label: 'Attack templates and presets', icon: SettingsSuggestRoundedIcon, detail: 'Use your saved troops and tools' },
    { label: 'Live Ops and battle reports', icon: ReceiptLongRoundedIcon, detail: 'Monitor activity and results' },
    { label: 'Castle defense and attack alerts', icon: SecurityRoundedIcon, detail: 'Protection and incoming attack tools' },
    { label: 'Recruitment and resources', icon: SpeedRoundedIcon, detail: 'Automate recurring account work' },
    { label: 'Commander and Castellan crafting', icon: CrownRoundedIcon, detail: 'Craft with your configured priorities' },
    { label: 'Groups and notifications', icon: GroupsRoundedIcon, detail: 'Organize bots and receive Discord alerts' }
]

const LICENSE_STEPS = [
    {
        number: '01',
        title: 'Choose simultaneous capacity',
        detail: 'Select how many bots must be able to run at the same time.'
    },
    {
        number: '02',
        title: 'Request activation',
        detail: 'Open Discord and send the account name together with the selected plan.'
    },
    {
        number: '03',
        title: 'License is applied',
        detail: 'The administrator activates or renews access on your existing account.'
    }
]

export const resolvePricingSubscription = (accountInfo, now = Date.now()) => {
    if (accountInfo?.hasLifetimeAccess) {
        return {
            tone: 'lifetime',
            label: 'Lifetime access',
            detail: 'Your account does not expire.'
        }
    }

    const expiration = new Date(accountInfo?.subscriptionEndsAt ?? '').getTime()
    if (!Number.isFinite(expiration)) {
        return {
            tone: 'inactive',
            label: 'No active subscription found',
            detail: 'Contact the administrator to activate a license.'
        }
    }

    if (expiration <= now) {
        return {
            tone: 'expired',
            label: 'Subscription expired',
            detail: 'Choose a plan to renew access.',
            expiration
        }
    }

    return {
        tone: 'active',
        label: 'Subscription active',
        detail: 'Your current access remains active.',
        expiration
    }
}

const subscriptionToneStyles = {
    lifetime: {
        color: '#f7e3aa',
        borderColor: 'rgba(244, 201, 93, 0.42)',
        backgroundColor: 'rgba(244, 201, 93, 0.09)'
    },
    active: {
        color: '#a7f3d0',
        borderColor: 'rgba(110, 212, 165, 0.4)',
        backgroundColor: 'rgba(110, 212, 165, 0.08)'
    },
    expired: {
        color: '#fecaca',
        borderColor: 'rgba(244, 132, 132, 0.4)',
        backgroundColor: 'rgba(244, 132, 132, 0.08)'
    },
    inactive: {
        color: '#cbd5e1',
        borderColor: 'rgba(156, 169, 187, 0.3)',
        backgroundColor: 'rgba(156, 169, 187, 0.06)'
    }
}

const SectionHeading = ({ eyebrow, title, detail, t }) => (
    <Stack spacing={0.7} sx={{ mb: 2.25 }}>
        <Typography
            variant="overline"
            sx={{ color: '#f4c95d', fontWeight: 850, lineHeight: 1.2 }}
        >
            {t(eyebrow)}
        </Typography>
        <Typography
            variant="h4"
            sx={{ fontSize: { xs: '1.45rem', md: '1.8rem' }, letterSpacing: 0 }}
        >
            {t(title)}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 780, lineHeight: 1.65 }}>
            {t(detail)}
        </Typography>
    </Stack>
)

function CapacityVisual({ plan, t }) {
    const visibleBots = Math.min(Number.parseInt(plan.concurrentBots, 10) || 10, 4)

    return (
        <Box
            sx={{
                p: 1.5,
                borderRadius: '8px',
                border: `1px solid ${plan.accent}38`,
                backgroundColor: `${plan.accent}0b`
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                        {t('RUNNING AT ONCE')}
                    </Typography>
                    <Typography sx={{ mt: 0.2, color: plan.accent, fontSize: '1.35rem', fontWeight: 850 }}>
                        {t(plan.bots)}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={-0.45} alignItems="center">
                    {Array.from({ length: visibleBots }, (_, index) => (
                        <Box
                            key={index}
                            sx={{
                                width: 30,
                                height: 30,
                                display: 'grid',
                                placeItems: 'center',
                                borderRadius: '50%',
                                color: '#08111f',
                                backgroundColor: plan.accent,
                                border: '2px solid #121c2b',
                                zIndex: visibleBots - index
                            }}
                        >
                            <BoltRoundedIcon sx={{ fontSize: 16 }} />
                        </Box>
                    ))}
                    {plan.concurrentBots.includes('+') && (
                        <Box
                            sx={{
                                width: 30,
                                height: 30,
                                display: 'grid',
                                placeItems: 'center',
                                borderRadius: '50%',
                                color: plan.accent,
                                backgroundColor: '#172335',
                                border: '2px solid #121c2b',
                                fontSize: '0.7rem',
                                fontWeight: 850
                            }}
                        >
                            +6
                        </Box>
                    )}
                </Stack>
            </Stack>
        </Box>
    )
}

function PlanCard({ plan, t, purchaseReady, purchaseStatusLoading, onChoose }) {
    return (
        <Box
            sx={{
                position: 'relative',
                height: '100%',
                minHeight: 500,
                display: 'flex',
                flexDirection: 'column',
                p: 2.25,
                pt: plan.recommended ? 3.3 : 2.25,
                borderRadius: '8px',
                border: `1px solid ${plan.recommended ? 'rgba(244, 201, 93, 0.66)' : 'rgba(255,255,255,0.1)'}`,
                backgroundColor: plan.recommended ? 'rgba(38, 34, 24, 0.72)' : 'rgba(18, 28, 43, 0.76)',
                boxShadow: plan.recommended ? '0 18px 44px rgba(3, 10, 20, 0.36)' : 'none',
                overflow: 'hidden',
                transition: 'transform 160ms ease, border-color 160ms ease, background-color 160ms ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: plan.accent,
                    backgroundColor: plan.recommended ? 'rgba(44, 39, 27, 0.88)' : 'rgba(23, 35, 53, 0.92)'
                }
            }}
        >
            <Box sx={{ height: 4, backgroundColor: plan.accent, position: 'absolute', top: 0, left: 0, right: 0 }} />
            {plan.recommended && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        px: 1.5,
                        py: 0.65,
                        color: '#17130a',
                        backgroundColor: '#f4c95d',
                        borderBottomLeftRadius: '8px',
                        fontSize: '0.68rem',
                        fontWeight: 900
                    }}
                >
                    {t('MOST POPULAR')}
                </Box>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box>
                    <Typography variant="h5" sx={{ fontSize: '1.2rem', fontWeight: 850 }}>
                        {t(plan.name)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.45, minHeight: 42 }}>
                        {t(plan.bestFor)}
                    </Typography>
                </Box>
                <Typography
                    sx={{
                        color: plan.accent,
                        fontSize: '0.78rem',
                        fontWeight: 850,
                        border: `1px solid ${plan.accent}55`,
                        borderRadius: '6px',
                        px: 0.9,
                        py: 0.45,
                        whiteSpace: 'nowrap'
                    }}
                >
                    {t(plan.bots)}
                </Typography>
            </Stack>

            <Box sx={{ my: 2 }}>
                <Stack direction="row" alignItems="baseline" spacing={0.45}>
                    <Typography sx={{ color: plan.accent, fontSize: '0.95rem', fontWeight: 850 }}>EUR</Typography>
                    <Typography sx={{ fontSize: '2.55rem', lineHeight: 1, fontWeight: 850 }}>
                        {plan.monthlyPrice}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t('/ month')}
                    </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {`EUR ${plan.perBotPrice} ${t('per running bot')}`}
                    </Typography>
                    {plan.savings && (
                        <Chip
                            size="small"
                            label={t(plan.savings)}
                            sx={{
                                height: 24,
                                color: plan.accent,
                                borderColor: `${plan.accent}55`,
                                backgroundColor: `${plan.accent}12`,
                                fontWeight: 800
                            }}
                        />
                    )}
                </Stack>
            </Box>

            <CapacityVisual plan={plan} t={t} />

            <Stack spacing={1.15} sx={{ py: 2, flex: 1 }}>
                {[
                    plan.description,
                    'All automation modules are included',
                    'All supported kingdoms are included',
                    'Monthly activation, no automatic charge'
                ].map(feature => (
                    <Stack key={feature} direction="row" spacing={0.9} alignItems="flex-start">
                        <CheckRoundedIcon sx={{ mt: 0.05, fontSize: 18, color: plan.accent, flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                            {t(feature)}
                        </Typography>
                    </Stack>
                ))}
            </Stack>

            <Button
                fullWidth
                variant={plan.recommended ? 'contained' : 'outlined'}
                disabled={!purchaseReady || purchaseStatusLoading}
                onClick={() => onChoose(plan)}
                startIcon={
                    purchaseStatusLoading ?
                        <CircularProgress size={16} color="inherit" /> :
                        <SendRoundedIcon />
                }
                sx={{
                    minHeight: 44,
                    borderRadius: '8px',
                    ...(plan.recommended ? {
                        color: '#17130a',
                        backgroundColor: '#f4c95d',
                        boxShadow: '0 10px 24px rgba(244, 201, 93, 0.18)',
                        '&:hover': {
                            backgroundColor: '#f7d779',
                            boxShadow: '0 12px 28px rgba(244, 201, 93, 0.24)'
                        }
                    } : {})
                }}
            >
                {purchaseStatusLoading ?
                    t('Checking Discord') :
                    purchaseReady ?
                        `${t('Request')} ${t(plan.name)}` :
                        t('Purchase unavailable')}
            </Button>
        </Box>
    )
}

export default function PricingPage({ accountInfo, appLanguage = 'en' }) {
    const subscription = resolvePricingSubscription(accountInfo)
    const toneStyle = subscriptionToneStyles[subscription.tone]
    const [purchaseStatus, setPurchaseStatus] = React.useState({
        loading: true,
        discordReady: false,
        recipientConfigured: false,
        guildConnected: false,
        botInviteUrl: '',
        pricingChannelConfigured: false,
        pricingChannelConnected: false,
        pricingThreadReady: false,
        pricingChannelName: '',
        pricingChannelUrl: '',
        canConfigureRecipient: false
    })
    const [selectedPlan, setSelectedPlan] = React.useState(null)
    const [purchaseSubmitting, setPurchaseSubmitting] = React.useState(false)
    const [purchaseDiscordContact, setPurchaseDiscordContact] = React.useState('')
    const [discordUserId, setDiscordUserId] = React.useState('')
    const [discordSetupSubmitting, setDiscordSetupSubmitting] = React.useState(false)
    const [feedback, setFeedback] = React.useState(null)
    const translationTexts = [
        'Pricing',
        'Choose by simultaneous bots, not by modules.',
        'Every plan contains the complete automation suite. The only difference is how many bots can be active at the same time.',
        'CURRENT LICENSE',
        'Current access',
        subscription.label,
        subscription.detail,
        'Expires',
        'ALL MODULES',
        'included in every plan',
        'MONTHLY',
        'manual activation',
        'FLEXIBLE',
        'upgrade when needed',
        'How the license works',
        'Choose the capacity you actually need',
        'The capacity refers to active bots running simultaneously. It does not remove modules from smaller plans.',
        'RUNNING AT ONCE',
        'MOST POPULAR',
        'Checking Discord',
        'Request',
        'Purchase unavailable',
        '/ month',
        'per running bot',
        'Save 20%',
        'Save 40%',
        'Best value',
        'Choose',
        'All automation modules are included',
        'All supported kingdoms are included',
        'Monthly activation, no automatic charge',
        'Compare plans',
        'Exact monthly comparison',
        'See the exact capacity and monthly cost before requesting activation.',
        'Plan',
        'Bots running together',
        'Monthly total',
        'Cost per bot',
        'Modules',
        'All included',
        'What every plan contains',
        'Complete automation suite',
        'No feature is removed when you choose a smaller concurrent license.',
        'All core attack modules',
        'Towers, camps, fortresses and events',
        'Multi-kingdom automation',
        'Configure every supported kingdom',
        'Attack templates and presets',
        'Use your saved troops and tools',
        'Live Ops and battle reports',
        'Monitor activity and results',
        'Castle defense and attack alerts',
        'Protection and incoming attack tools',
        'Recruitment and resources',
        'Automate recurring account work',
        'Commander and Castellan crafting',
        'Craft with your configured priorities',
        'Groups and notifications',
        'Organize bots and receive Discord alerts',
        'Activation in three clear steps',
        'From plan selection to active license',
        'There is no automatic payment inside the dashboard. Activation is confirmed by the administrator.',
        ...LICENSE_STEPS.flatMap(step => [step.title, step.detail]),
        'Choose a package',
        'Discord purchase notifications',
        'Discord bot connection required',
        'Configure discordToken and discordClientId on the server before connecting the administrator recipient.',
        'Install the Discord bot on your server',
        'The bot is connected, but it is not a member of the configured Discord server.',
        'Add bot to Discord server',
        'Pricing channel setup required',
        'Configure the pricing channel on the server before accepting purchase requests.',
        'Pricing thread permissions required',
        'Update the Discord bot permissions so it can create a separate discussion thread for every buyer.',
        'Update bot permissions',
        'Open pricing channel',
        'Requests will be sent privately and posted in the pricing channel with a separate discussion thread.',
        'Connect the administrator Discord account to receive private purchase requests.',
        'Discord User ID',
        'Connect and test',
        'Purchase messages active',
        'The bot can send private purchase requests to the administrator.',
        'Purchase requests are temporarily unavailable because no administrator Discord recipient is connected.',
        'Confirm purchase request',
        'The administrator will receive this package in the first line of the private Discord message.',
        'Selected package',
        'Your Discord contact',
        'Discord username or User ID',
        'Required so the administrator can contact you about payment.',
        'Use your numeric Discord User ID to be added automatically to the pricing discussion.',
        'Simultaneous bots',
        'Monthly price',
        'Cancel',
        'Send private request',
        'Sending request',
        'Purchase request sent for the {plan} package. The pricing discussion is ready.',
        'Discord notifications connected for {recipient}.',
        'Important',
        'Prices are monthly. A plan controls simultaneous bot capacity; every listed module remains included.',
        'Solo',
        'Duo',
        'Command',
        'No Limit',
        '1 Bot',
        '2 Bots',
        '4 Bots',
        '10+ Bots',
        ...PRICING_PLANS.flatMap(plan => [plan.description, plan.bestFor])
    ]
    const t = useAutoTranslate(appLanguage, translationTexts)
    const purchaseReady = Boolean(
        purchaseStatus.discordReady &&
        purchaseStatus.recipientConfigured &&
        purchaseStatus.guildConnected &&
        purchaseStatus.pricingChannelConnected &&
        purchaseStatus.pricingThreadReady
    )
    const loadPurchaseStatus = React.useCallback(async () => {
        try {
            const response = await fetch('/pricing-purchase-status', {
                headers: {
                    Accept: 'application/json'
                }
            })
            const payload = await response.json()
            if (!response.ok)
                throw new Error(payload?.error || 'Could not load purchase status.')

            setPurchaseStatus({
                loading: false,
                discordReady: Boolean(payload?.discordReady),
                recipientConfigured: Boolean(payload?.recipientConfigured),
                guildConnected: Boolean(payload?.guildConnected),
                botInviteUrl: String(payload?.botInviteUrl ?? ''),
                pricingChannelConfigured: Boolean(payload?.pricingChannelConfigured),
                pricingChannelConnected: Boolean(payload?.pricingChannelConnected),
                pricingThreadReady: Boolean(payload?.pricingThreadReady),
                pricingChannelName: String(payload?.pricingChannelName ?? ''),
                pricingChannelUrl: String(payload?.pricingChannelUrl ?? ''),
                canConfigureRecipient: Boolean(payload?.canConfigureRecipient)
            })
        }
        catch (error) {
            setPurchaseStatus({
                loading: false,
                discordReady: false,
                recipientConfigured: false,
                guildConnected: false,
                botInviteUrl: '',
                pricingChannelConfigured: false,
                pricingChannelConnected: false,
                pricingThreadReady: false,
                pricingChannelName: '',
                pricingChannelUrl: '',
                canConfigureRecipient: Boolean(accountInfo?.isAdmin)
            })
            setFeedback({
                severity: 'error',
                message: String(error?.message ?? 'Could not load purchase status.')
            })
        }
    }, [accountInfo?.isAdmin])

    React.useEffect(() => {
        void loadPurchaseStatus()
    }, [loadPurchaseStatus])

    const handleConnectDiscordRecipient = async () => {
        setDiscordSetupSubmitting(true)
        setFeedback(null)

        try {
            const response = await fetch('/pricing-discord-recipient', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-GGE-Pricing-Request': '1'
                },
                body: JSON.stringify({ discordUserId: discordUserId.trim() })
            })
            const payload = await response.json()
            if (!response.ok)
                throw new Error(payload?.error || 'Could not connect Discord notifications.')

            setFeedback({
                severity: 'success',
                message: t('Discord notifications connected for {recipient}.')
                    .replace('{recipient}', String(payload?.recipient ?? 'administrator'))
            })
            setDiscordUserId('')
            await loadPurchaseStatus()
        }
        catch (error) {
            setFeedback({
                severity: 'error',
                message: String(error?.message ?? 'Could not connect Discord notifications.')
            })
        }
        finally {
            setDiscordSetupSubmitting(false)
        }
    }

    const handleSendPurchaseRequest = async () => {
        if (!selectedPlan)
            return

        setPurchaseSubmitting(true)
        setFeedback(null)

        try {
            const response = await fetch('/pricing-purchase-request', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-GGE-Pricing-Request': '1'
                },
                body: JSON.stringify({
                    plan: selectedPlan.key,
                    discordContact: purchaseDiscordContact.trim()
                })
            })
            const payload = await response.json()
            if (!response.ok)
                throw new Error(payload?.error || 'Could not send the purchase request.')

            setFeedback({
                severity: 'success',
                message: t('Purchase request sent for the {plan} package.')
                    .replace('{plan}', String(payload?.plan ?? selectedPlan.name))
            })
            setSelectedPlan(null)
        }
        catch (error) {
            setFeedback({
                severity: 'error',
                message: String(error?.message ?? 'Could not send the purchase request.')
            })
        }
        finally {
            setPurchaseSubmitting(false)
        }
    }

    const expirationLabel = subscription.expiration ?
        new Intl.DateTimeFormat(appLanguage || 'en', {
            dateStyle: 'medium',
            timeStyle: 'short'
        }).format(subscription.expiration) :
        ''

    return (
        <Box sx={{ display: 'grid', gap: { xs: 3.5, md: 5 }, pb: 4 }}>
            {feedback && (
                <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ borderRadius: '8px' }}>
                    {feedback.message}
                </Alert>
            )}

            <Box component="section" sx={{ pt: { xs: 0.5, md: 1.5 } }}>
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between">
                    <Stack spacing={1.3} sx={{ maxWidth: 820 }}>
                        <Stack direction="row" spacing={1.2} alignItems="center">
                            <Box
                                sx={{
                                    width: 42,
                                    height: 42,
                                    display: 'grid',
                                    placeItems: 'center',
                                    borderRadius: '8px',
                                    color: '#101621',
                                    backgroundColor: '#f4c95d',
                                    boxShadow: '0 8px 22px rgba(244, 201, 93, 0.18)'
                                }}
                            >
                                <PaidRoundedIcon />
                            </Box>
                            <Typography variant="h3" sx={{ fontSize: { xs: '2rem', md: '2.55rem' }, letterSpacing: 0 }}>
                                {t('Pricing')}
                            </Typography>
                        </Stack>
                        <Typography variant="h5" sx={{ fontSize: { xs: '1.15rem', md: '1.45rem' }, lineHeight: 1.4 }}>
                            {t('Choose by simultaneous bots, not by modules.')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
                            {t('Every plan contains the complete automation suite. The only difference is how many bots can be active at the same time.')}
                        </Typography>
                    </Stack>

                    <Box
                        sx={{
                            width: { xs: '100%', lg: 340 },
                            flexShrink: 0,
                            p: 2.25,
                            borderRadius: '8px',
                            border: '1px solid',
                            ...toneStyle
                        }}
                    >
                        <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 900 }}>
                            {t('CURRENT LICENSE')}
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 0.65, color: 'text.primary', fontSize: '1.05rem' }}>
                            {t(subscription.label)}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.55, color: 'text.secondary', lineHeight: 1.55 }}>
                            {t(subscription.detail)}
                        </Typography>
                        {expirationLabel && (
                            <Typography variant="body2" sx={{ mt: 1.25, color: 'inherit', fontWeight: 800 }}>
                                {`${t('Expires')}: ${expirationLabel}`}
                            </Typography>
                        )}
                    </Box>
                </Stack>

                <Box
                    sx={{
                        mt: 3,
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                        borderTop: '1px solid rgba(255,255,255,0.08)',
                        borderBottom: '1px solid rgba(255,255,255,0.08)'
                    }}
                >
                    {[
                        { label: 'ALL MODULES', value: 'included in every plan', color: '#6ed4a5' },
                        { label: 'MONTHLY', value: 'manual activation', color: '#73c9f1' },
                        { label: 'FLEXIBLE', value: 'upgrade when needed', color: '#f4c95d' }
                    ].map((item, index) => (
                        <Box
                            key={item.label}
                            sx={{
                                px: { xs: 0, sm: 2 },
                                py: 1.6,
                                borderLeft: { xs: 0, sm: index ? '1px solid rgba(255,255,255,0.08)' : 0 }
                            }}
                        >
                            <Typography variant="caption" sx={{ color: item.color, fontWeight: 900 }}>
                                {t(item.label)}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.25, color: 'text.secondary' }}>
                                {t(item.value)}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {!purchaseStatus.loading && purchaseStatus.canConfigureRecipient && !purchaseStatus.discordReady && (
                <Alert severity="error" sx={{ borderRadius: '8px' }}>
                    <Typography variant="body2" sx={{ fontWeight: 850 }}>
                        {t('Discord bot connection required')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {t('Configure discordToken and discordClientId on the server before connecting the administrator recipient.')}
                    </Typography>
                </Alert>
            )}

            {!purchaseStatus.loading &&
                purchaseStatus.canConfigureRecipient &&
                purchaseStatus.discordReady &&
                purchaseStatus.recipientConfigured &&
                !purchaseStatus.guildConnected && (
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    sx={{
                        p: 2,
                        borderRadius: '8px',
                        border: '1px solid rgba(240, 179, 109, 0.36)',
                        backgroundColor: 'rgba(240, 179, 109, 0.07)'
                    }}
                >
                    <Box>
                        <Typography variant="body2" sx={{ color: '#ffd6a5', fontWeight: 850 }}>
                            {t('Install the Discord bot on your server')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.45, color: 'text.secondary', lineHeight: 1.55 }}>
                            {t('The bot is connected, but it is not a member of the configured Discord server.')}
                        </Typography>
                    </Box>
                    <Button
                        component="a"
                        href={purchaseStatus.botInviteUrl || undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        disabled={!purchaseStatus.botInviteUrl}
                        variant="contained"
                        endIcon={<ExternalLinkRoundedIcon />}
                        sx={{ flexShrink: 0, minHeight: 42, borderRadius: '8px' }}
                    >
                        {t('Add bot to Discord server')}
                    </Button>
                </Stack>
            )}

            {!purchaseStatus.loading &&
                purchaseStatus.canConfigureRecipient &&
                purchaseStatus.discordReady &&
                purchaseStatus.guildConnected &&
                !purchaseStatus.pricingChannelConfigured && (
                <Alert severity="error" sx={{ borderRadius: '8px' }}>
                    <Typography variant="body2" sx={{ fontWeight: 850 }}>
                        {t('Pricing channel setup required')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {t('Configure the pricing channel on the server before accepting purchase requests.')}
                    </Typography>
                </Alert>
            )}

            {!purchaseStatus.loading &&
                purchaseStatus.canConfigureRecipient &&
                purchaseStatus.discordReady &&
                purchaseStatus.guildConnected &&
                purchaseStatus.pricingChannelConnected &&
                !purchaseStatus.pricingThreadReady && (
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    sx={{
                        p: 2,
                        borderRadius: '8px',
                        border: '1px solid rgba(240, 179, 109, 0.36)',
                        backgroundColor: 'rgba(240, 179, 109, 0.07)'
                    }}
                >
                    <Box>
                        <Typography variant="body2" sx={{ color: '#ffd6a5', fontWeight: 850 }}>
                            {t('Pricing thread permissions required')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.45, color: 'text.secondary', lineHeight: 1.55 }}>
                            {t('Update the Discord bot permissions so it can create a separate discussion thread for every buyer.')}
                        </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        {purchaseStatus.pricingChannelUrl && (
                            <Button
                                component="a"
                                href={purchaseStatus.pricingChannelUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="outlined"
                                endIcon={<ExternalLinkRoundedIcon />}
                                sx={{ minHeight: 42, borderRadius: '8px' }}
                            >
                                {t('Open pricing channel')}
                            </Button>
                        )}
                        <Button
                            component="a"
                            href={purchaseStatus.botInviteUrl || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={!purchaseStatus.botInviteUrl}
                            variant="contained"
                            endIcon={<ExternalLinkRoundedIcon />}
                            sx={{ minHeight: 42, borderRadius: '8px' }}
                        >
                            {t('Update bot permissions')}
                        </Button>
                    </Stack>
                </Stack>
            )}

            {!purchaseStatus.loading && purchaseStatus.canConfigureRecipient && purchaseStatus.discordReady && !purchaseStatus.recipientConfigured && (
                <Box
                    component="section"
                    sx={{
                        p: 2,
                        borderRadius: '8px',
                        border: '1px solid rgba(115, 201, 241, 0.32)',
                        backgroundColor: 'rgba(115, 201, 241, 0.06)'
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        justifyContent="space-between"
                    >
                        <Box sx={{ maxWidth: 680 }}>
                            <Stack direction="row" spacing={0.8} alignItems="center">
                                <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                                    {t('Discord purchase notifications')}
                                </Typography>
                                <Tooltip title="Discord Settings > Advanced > Developer Mode, then right-click your profile and select Copy User ID.">
                                    <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                </Tooltip>
                            </Stack>
                            <Typography variant="body2" sx={{ mt: 0.55, color: 'text.secondary', lineHeight: 1.55 }}>
                                {t('Connect the administrator Discord account to receive private purchase requests.')}
                            </Typography>
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ minWidth: { md: 470 } }}>
                            <TextField
                                size="small"
                                label={t('Discord User ID')}
                                value={discordUserId}
                                onChange={event => setDiscordUserId(event.target.value.replace(/\D/g, '').slice(0, 22))}
                                inputProps={{ inputMode: 'numeric' }}
                                sx={{ flex: 1 }}
                            />
                            <Button
                                variant="contained"
                                disabled={discordSetupSubmitting || discordUserId.length < 15}
                                onClick={handleConnectDiscordRecipient}
                                startIcon={
                                    discordSetupSubmitting ?
                                        <CircularProgress size={16} color="inherit" /> :
                                        <SendRoundedIcon />
                                }
                                sx={{ minHeight: 40, borderRadius: '8px' }}
                            >
                                {t('Connect and test')}
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            )}

            {!purchaseStatus.loading && purchaseStatus.canConfigureRecipient && purchaseReady && (
                <Alert severity="success" icon={<CheckRoundedIcon />} sx={{ borderRadius: '8px' }}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {t('Purchase messages active')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {t('Requests will be sent privately and posted in the pricing channel with a separate discussion thread.')}
                    </Typography>
                </Alert>
            )}

            {!purchaseStatus.loading && !purchaseStatus.canConfigureRecipient && !purchaseReady && (
                <Alert severity="warning" sx={{ borderRadius: '8px' }}>
                    {t('Purchase requests are temporarily unavailable because no administrator Discord recipient is connected.')}
                </Alert>
            )}

            <Box component="section" id="pricing-plans">
                <SectionHeading
                    eyebrow="How the license works"
                    title="Choose the capacity you actually need"
                    detail="The capacity refers to active bots running simultaneously. It does not remove modules from smaller plans."
                    t={t}
                />
                <Grid container spacing={2}>
                    {PRICING_PLANS.map(plan => (
                        <Grid item xs={12} md={6} xl={3} key={plan.key}>
                            <PlanCard
                                plan={plan}
                                t={t}
                                purchaseReady={purchaseReady}
                                purchaseStatusLoading={purchaseStatus.loading}
                                onChoose={setSelectedPlan}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Box component="section">
                <SectionHeading
                    eyebrow="Compare plans"
                    title="Exact monthly comparison"
                    detail="See the exact capacity and monthly cost before requesting activation."
                    t={t}
                />
                <TableContainer
                    sx={{
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.09)',
                        backgroundColor: 'rgba(18, 28, 43, 0.65)'
                    }}
                >
                    <Table sx={{ minWidth: 720 }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'rgba(255,255,255,0.035)' }}>
                                {['Plan', 'Bots running together', 'Monthly total', 'Cost per bot', 'Modules'].map(label => (
                                    <TableCell key={label} sx={{ color: 'text.secondary', fontWeight: 850 }}>
                                        {t(label)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {PRICING_PLANS.map(plan => (
                                <TableRow
                                    key={plan.key}
                                    sx={{
                                        '&:last-child td': { borderBottom: 0 },
                                        backgroundColor: plan.recommended ? 'rgba(244, 201, 93, 0.045)' : 'transparent'
                                    }}
                                >
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Box sx={{ width: 4, height: 28, borderRadius: '2px', backgroundColor: plan.accent }} />
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 850 }}>{t(plan.name)}</Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t(plan.bestFor)}</Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 800 }}>{t(plan.bots)}</TableCell>
                                    <TableCell sx={{ fontWeight: 800 }}>{`EUR ${plan.monthlyPrice}`}</TableCell>
                                    <TableCell>{`EUR ${plan.perBotPrice}`}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            icon={<CheckRoundedIcon />}
                                            label={t('All included')}
                                            sx={{ color: '#a7f3d0', borderColor: 'rgba(110,212,165,0.28)' }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Box component="section">
                <SectionHeading
                    eyebrow="What every plan contains"
                    title="Complete automation suite"
                    detail="No feature is removed when you choose a smaller concurrent license."
                    t={t}
                />
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                        gap: 1
                    }}
                >
                    {INCLUDED_FEATURES.map(feature => {
                        const FeatureIcon = feature.icon
                        return (
                            <Stack
                                key={feature.label}
                                direction="row"
                                spacing={1.15}
                                alignItems="flex-start"
                                sx={{
                                    minHeight: 88,
                                    p: 1.5,
                                    borderRadius: '8px',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    backgroundColor: 'rgba(255,255,255,0.028)'
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        flexShrink: 0,
                                        display: 'grid',
                                        placeItems: 'center',
                                        borderRadius: '8px',
                                        color: '#a9c3ff',
                                        backgroundColor: 'rgba(111, 157, 255, 0.11)'
                                    }}
                                >
                                    <FeatureIcon sx={{ fontSize: 20 }} />
                                </Box>
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.4 }}>
                                        {t(feature.label)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.4, color: 'text.secondary', lineHeight: 1.45 }}>
                                        {t(feature.detail)}
                                    </Typography>
                                </Box>
                            </Stack>
                        )
                    })}
                </Box>
            </Box>

            <Box component="section">
                <SectionHeading
                    eyebrow="Activation in three clear steps"
                    title="From plan selection to active license"
                    detail="There is no automatic payment inside the dashboard. Activation is confirmed by the administrator."
                    t={t}
                />
                <Grid container spacing={1.5}>
                    {LICENSE_STEPS.map(step => (
                        <Grid item xs={12} md={4} key={step.number}>
                            <Stack
                                direction="row"
                                spacing={1.5}
                                sx={{
                                    height: '100%',
                                    p: 2,
                                    borderRadius: '8px',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    backgroundColor: 'rgba(255,255,255,0.025)'
                                }}
                            >
                                <Typography sx={{ color: '#f4c95d', fontSize: '1.25rem', fontWeight: 900 }}>
                                    {step.number}
                                </Typography>
                                <Box>
                                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                                        {t(step.title)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.65, color: 'text.secondary', lineHeight: 1.6 }}>
                                        {t(step.detail)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>
                    ))}
                </Grid>

                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: '8px',
                        border: '1px solid rgba(244,201,93,0.28)',
                        backgroundColor: 'rgba(244,201,93,0.06)'
                    }}
                >
                    <Box>
                        <Typography variant="body2" sx={{ color: '#f7e3aa', fontWeight: 850 }}>
                            {t('Important')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.45, color: 'text.secondary', lineHeight: 1.6 }}>
                            {t('Prices are monthly. A plan controls simultaneous bot capacity; every listed module remains included.')}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<PaidRoundedIcon />}
                        onClick={() => document.getElementById('pricing-plans')?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                        })}
                        sx={{
                            flexShrink: 0,
                            minHeight: 44,
                            borderRadius: '8px',
                            color: '#17130a',
                            backgroundColor: '#f4c95d',
                            '&:hover': { backgroundColor: '#f7d779' }
                        }}
                    >
                        {t('Choose a package')}
                    </Button>
                </Stack>
            </Box>

            <Dialog
                open={Boolean(selectedPlan)}
                onClose={purchaseSubmitting ? undefined : () => setSelectedPlan(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '8px' } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    {t('Confirm purchase request')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                        {t('The administrator will receive this package in the first line of the private Discord message.')}
                    </Typography>
                    <TextField
                        required
                        fullWidth
                        autoFocus
                        size="small"
                        label={t('Discord username or User ID')}
                        value={purchaseDiscordContact}
                        onChange={event => setPurchaseDiscordContact(
                            event.target.value
                                .replace(/[\r\n\t]+/g, ' ')
                                .slice(0, 80)
                        )}
                        helperText={`${t('Required so the administrator can contact you about payment.')} ${t('Use your numeric Discord User ID to be added automatically to the pricing discussion.')}`}
                        inputProps={{ maxLength: 80 }}
                        sx={{ mt: 2 }}
                    />
                    {selectedPlan && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                borderRadius: '8px',
                                border: `1px solid ${selectedPlan.accent}55`,
                                backgroundColor: `${selectedPlan.accent}0c`
                            }}
                        >
                            <Stack spacing={1.1}>
                                <Stack direction="row" justifyContent="space-between" spacing={2}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('Selected package')}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 850 }}>{t(selectedPlan.name)}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={2}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('Your Discord contact')}</Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            maxWidth: '58%',
                                            overflowWrap: 'anywhere',
                                            textAlign: 'right',
                                            fontWeight: 850
                                        }}
                                    >
                                        {purchaseDiscordContact.trim() || '-'}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={2}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('Simultaneous bots')}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 850 }}>{t(selectedPlan.bots)}</Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" spacing={2}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('Monthly price')}</Typography>
                                    <Typography variant="body2" sx={{ color: selectedPlan.accent, fontWeight: 900 }}>
                                        {`EUR ${selectedPlan.monthlyPrice}`}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        disabled={purchaseSubmitting}
                        onClick={() => setSelectedPlan(null)}
                        sx={{ borderRadius: '8px' }}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={purchaseSubmitting || purchaseDiscordContact.trim().length < 2}
                        onClick={handleSendPurchaseRequest}
                        startIcon={
                            purchaseSubmitting ?
                                <CircularProgress size={16} color="inherit" /> :
                                <SendRoundedIcon />
                        }
                        sx={{
                            borderRadius: '8px',
                            color: '#17130a',
                            backgroundColor: '#f4c95d',
                            '&:hover': { backgroundColor: '#f7d779' }
                        }}
                    >
                        {purchaseSubmitting ? t('Sending request') : t('Send private request')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
